/**
 * Environment Configuration Utility
 * 
 * This utility helps determine the current environment (development, staging, production)
 * and provides the appropriate backend URL and other environment-specific settings.
 * 
 * Environment Detection Priority:
 * 1. VITE_APP_ENV (explicitly set)
 * 2. Vite MODE (from --mode flag or .env file)
 * 3. Hostname-based detection (for runtime detection)
 * 4. Defaults to "development"
 */

export type Environment = "development" | "staging" | "production";

interface EnvironmentConfig {
  environment: Environment;
  backendUrl: string;
  isProduction: boolean;
  isStaging: boolean;
  isDevelopment: boolean;
}

/**
 * Detects the current environment based on:
 * - VITE_APP_ENV environment variable
 * - Vite MODE
 * - Current hostname (for runtime detection)
 */
function detectEnvironment(): Environment {
  // 1. Check explicit environment variable
  const explicitEnv = import.meta.env.VITE_APP_ENV?.toLowerCase();
  if (explicitEnv === "production" || explicitEnv === "staging" || explicitEnv === "development") {
    return explicitEnv;
  }

  // 2. Check Vite MODE
  const viteMode = import.meta.env.MODE?.toLowerCase();
  if (viteMode === "production" || viteMode === "staging" || viteMode === "development") {
    return viteMode;
  }

  // 3. Runtime hostname detection (useful for Cloudflare Pages with different domains)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    
    // Production domain patterns
    if (hostname === "app.forgeworks.ai" || hostname === "forgeworks.ai") {
      return "production";
    }
    
    // Staging domain patterns (adjust based on your staging domain)
    if (hostname.includes("staging") || hostname.includes("stage") || hostname === "staging.forgeworks.ai") {
      return "staging";
    }
    
    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.startsWith("192.168.") || hostname.startsWith("10.")) {
      return "development";
    }
  }

  // 4. Default to development
  return "development";
}

/**
 * Gets the backend URL based on the current environment
 */
function getBackendUrl(environment: Environment): string {
  // 1. Check for explicit backend URL override
  const explicitUrl = import.meta.env.VITE_APP_BACKEND_URL;
  if (explicitUrl) {
    return explicitUrl;
  }

  // 2. Environment-specific defaults
  switch (environment) {
    case "production":
      return import.meta.env.VITE_APP_BACKEND_URL_PROD || "https://api.forgeworks.ai";
    case "staging":
      return import.meta.env.VITE_APP_BACKEND_URL_STAGING || "https://api-staging.forgeworks.ai";
    case "development":
    default:
      return "http://localhost:8080";
  }
}

/**
 * Gets the complete environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const environment = detectEnvironment();
  const backendUrl = getBackendUrl(environment);

  return {
    environment,
    backendUrl,
    isProduction: environment === "production",
    isStaging: environment === "staging",
    isDevelopment: environment === "development",
  };
}

// Export singleton instance
let envConfig: EnvironmentConfig | null = null;

/**
 * Gets or creates the environment configuration (singleton)
 */
export function getEnv(): EnvironmentConfig {
  if (!envConfig) {
    envConfig = getEnvironmentConfig();
  }
  return envConfig;
}

/**
 * Resets the environment configuration (useful for testing)
 */
export function resetEnv(): void {
  envConfig = null;
}

// Export convenience getters
export const env = getEnv();
export const isProduction = env.isProduction;
export const isStaging = env.isStaging;
export const isDevelopment = env.isDevelopment;
export const backendUrl = env.backendUrl;
export const environment = env.environment;

